import { useEffect, useRef, useState } from "react";

type UseSmoothLoadingOptions = {
  loading: boolean;
  showDelayMs: number;
  minVisibleMs: number;
};

export const useSmoothLoading = ({ loading, showDelayMs, minVisibleMs }: UseSmoothLoadingOptions) => {
  const [smoothLoading, setSmoothLoading] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (loading) {
      showTimerRef.current = setTimeout(() => {
        setSmoothLoading(true);
        shownAtRef.current = Date.now();
      }, showDelayMs);
      return;
    }

    if (!smoothLoading) return;

    const shownAt = shownAtRef.current ?? Date.now();
    const elapsed = Date.now() - shownAt;
    const remaining = Math.max(0, minVisibleMs - elapsed);

    hideTimerRef.current = setTimeout(() => {
      setSmoothLoading(false);
      shownAtRef.current = null;
    }, remaining);
  }, [loading, smoothLoading, minVisibleMs, showDelayMs]);

  useEffect(
    () => () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  return smoothLoading;
};
